import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getAtividadesCarro } from '../../../api/atividadeCarroApi';
import MotivoDaParada, {
  motivoDaParadaParaEnviar,
  pendenciaDoMotivo,
  useMotivosDaParada,
} from '../../motivo-da-parada/MotivoDaParada';
import { getCar, getCarStatusHistory } from '../../../api/carsApi';
import { searchCollaborators } from '../../../api/collaboratorsApi';
import { getColaboradoresElegiveis } from '../../../api/especialidadesApi';
import { getLatestChecklistSessionByCar } from '../../../api/checklistsApi';
import { useEsteiras } from '../../../hooks/useEsteiras';
import { notify } from '../../../shared/feedback/notify';
import { casaComBusca } from '../../../shared/texto/busca-sem-acento';
import { AtividadeEstado, isServicoEmAberto } from '../../../interfaces/AtividadeCarro';
import {
  ESCOLHA_PADRAO,
  TEXTOS_ESCOLHA_COLABORADOR,
  type EscolhaColaborador,
} from '../../../shared/ui/escolha-colaborador';

import './StatusModal.css';

interface PlannedActivity {
  id?: string;
  esteiraId?: string;
  /** Legado (esteira por chave) — o backend não grava mais; ver `esteiraId`. */
  status?: string;
  order: number | null;
  /** at-46: estado de execução do serviço nesta esteira. */
  estado?: AtividadeEstado;
}

interface StatusModalProps {
  isOpen: boolean;
  carId?: string | null;
  selectedStatus: string | null;
  setSelectedStatus: (status: string | null) => void;
  /**
   * at-46: `desfechoServicoAtual` = o que acontece com o serviço da esteira de origem.
   * at-93: `deixarSemColaborador` = o Chefe escolheu "Não atribuir" (o padrão) e o veículo deve
   * seguir sem colaborador nenhum. Nunca vem junto com `collaboratorId`.
   */
  onConfirm: (
    estimatedDaysInStatus: number | undefined,
    collaboratorId?: string,
    desfechoServicoAtual?: 'CONCLUIDO' | 'PAUSADO',
    deixarSemColaborador?: boolean,
    /** at-134 (ADR 0065): por que o serviço parou, quando o desfecho é PAUSADO. */
    motivoDaParada?: { motivoParadaId?: string; pausaObservacao?: string },
  ) => void;
  onCancel: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  isSidebarOpen: boolean;
  isChefe?: boolean;
}

const StatusModal: React.FC<StatusModalProps> = ({
  isOpen,
  carId,
  selectedStatus,
  setSelectedStatus,
  onConfirm,
  onCancel,
  sidebarRef,
  isChefe = false,
}) => {
  const { esteirasAtivas } = useEsteiras();
  const modalRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [estimatedDaysInStatus, setEstimatedDaysInStatus] = useState<number>(4);
  // Default: usar o SLA configurado na esteira destino (evita o "4" silencioso errado)
  const [useDefaultDays, setUseDefaultDays] = useState(true);
  const [plannedActivities, setPlannedActivities] = useState<PlannedActivity[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [carData, setCarData] = useState<any>(null);
  const [currentCarStatus, setCurrentCarStatus] = useState<string | null>(null);
  const [approvedStatuses, setApprovedStatuses] = useState<string[]>([]);

  const [collaboratorSearch, setCollaboratorSearch] = useState('');
  const [collaboratorResults, setCollaboratorResults] = useState<any[]>([]);
  /**
   * at-93: nasce em "Não atribuir" — o veículo vai para a esteira nova SEM colaborador, a menos
   * que o Chefe diga o contrário.
   *
   * Antes, "nada escolhido" significava "os vinculados continuam", e era o padrão silencioso: o
   * Chefe movia o veículo e levava o colaborador atual junto sem perceber (pedido do Victor,
   * 07/09/2026). O padrão agora é o desfecho seguro, e manter alguém é ato explícito.
   */
  const [escolhaColaborador, setEscolhaColaborador] = useState<EscolhaColaborador>(ESCOLHA_PADRAO);
  const [searchingCollaborator, setSearchingCollaborator] = useState(false);
  /**
   * A lista de colaboradores tem estado de aberta/fechada desde 28/09/2026 (relato do usuário na
   * placa RVT2C32: *"está pedindo para atribuir um colaborador"*).
   *
   * O que acontecia: desde 15/09/2026 o campo vazio lista todos os elegíveis — pensado para as
   * esteiras de TRABALHO, onde a especialidade restringe e a lista tem três nomes. Em
   * **Indenização Integral** ela é a oficina inteira, porque esteira de fluxo livre não é
   * restringida por especialidade (ADR 0038/0029) — e ela é a **única** esteira de sistema com
   * `permiteColaborador = true`, medido em produção. Uma lista `position: absolute` de 240px
   * abria sozinha sobre o botão de confirmar, e **nada** a fechava: não havia `onBlur`, estado de
   * aberto nem clique fora. O Chefe lia aquilo como exigência, com "Não atribuir" já marcado.
   *
   * 🔴 A decisão de 15/09 CONTINUA VALENDO onde ela foi tomada: em esteira de trabalho a lista
   * segue aparecendo sem ninguém digitar (há sentinela). O que mudou é (a) em esteira de fluxo
   * livre ela espera o Chefe tocar no campo, e (b) clicar fora fecha — em qualquer esteira.
   */
  const [listaColaboradoresAberta, setListaColaboradoresAberta] = useState(false);
  const buscaColaboradorRef = useRef<HTMLDivElement>(null);
  const [confirmStep, setConfirmStep] = useState(false);

  /**
   * at-62: ids elegíveis para a esteira DESTINO selecionada. `null` = sem restrição (regra inerte
   * ou consulta indisponível — fail-open).
   *
   * A esteira aqui é a de **destino**, não a atual: quem entra no veículo vai trabalhar na esteira
   * nova. Era o furo do primeiro corte da at-62 — o campo listava a oficina inteira, o Chefe
   * escolhia alguém sem a especialidade da esteira nova e a atribuição falhava depois de o veículo
   * já ter sido movido.
   */
  const [idsElegiveisDestino, setIdsElegiveisDestino] = useState<string[] | null>(null);
  /**
   * A lista COMPLETA de quem pode trabalhar no destino — vira a fonte do campo de busca
   * (decisão do Victor, 15/09/2026: o seletor mostra só quem pode trabalhar).
   *
   * ⚠️ Por que trocar a FONTE em vez de filtrar o resultado da busca: `searchCollaborators`
   * devolve 6 nomes por consulta, sobre a oficina inteira. Filtrando depois, uma busca em que os
   * 6 fossem inelegíveis mostraria "nenhum colaborador encontrado" — mentira, porque existe gente
   * elegível fora daqueles 6. A lista de elegíveis não é paginada, então filtrar nela é exato.
   *
   * `null` = sem restrição (regra inerte ou consulta que falhou): a busca no backend continua
   * como sempre.
   */
  const [elegiveisDestino, setElegiveisDestino] = useState<
    Array<{ id: string; fullName: string | null }> | null
  >(null);
  const [elegibilidadeIndisponivel, setElegibilidadeIndisponivel] = useState(false);

  // at-46: desfecho do serviço da esteira ATUAL quando o veículo sai dela.
  const [desfechoServicoAtual, setDesfechoServicoAtual] = useState<'CONCLUIDO' | 'PAUSADO' | null>(null);
  // at-134 (ADR 0065): o motivo da parada quando o Chefe deixa o serviço inacabado.
  const [motivoParadaId, setMotivoParadaId] = useState('');
  const [detalheDaParada, setDetalheDaParada] = useState('');
  const { motivos: motivosDaParada, estado: estadoDosMotivos } = useMotivosDaParada(isOpen && isChefe);
  const pendenciaDaParada =
    desfechoServicoAtual === 'PAUSADO'
      ? pendenciaDoMotivo(estadoDosMotivos, motivosDaParada, motivoParadaId, detalheDaParada)
      : null;

  const targetEst = useMemo(() => {
    return esteirasAtivas.find((e) => e.id === selectedStatus || e.chave === selectedStatus);
  }, [esteirasAtivas, selectedStatus]);

  const showCollaboratorPicker = isChefe && !!selectedStatus && targetEst?.permiteColaborador === true;
  const defaultDays = targetEst?.slaDiasPadrao ?? 4;

  /** Colaboradores JÁ vinculados ao veículo (at-45: são N). */
  const colaboradoresVinculados: Array<{ id: string; fullName: string | null }> =
    carData?.colaboradores ?? [];
  /**
   * at-93: sem ninguém vinculado, "Não atribuir" e "Manter os atuais" descrevem o MESMO estado —
   * oferecer as duas faria o Chefe escolher entre duas coisas iguais.
   */
  const podeManterColaboradores = colaboradoresVinculados.length > 0;
  const colaboradorEscolhido =
    escolhaColaborador.modo === 'ADICIONAR' ? escolhaColaborador : null;

  /**
   * at-46: o serviço da esteira atual está em aberto (em andamento ou já inacabado)? Só nesse
   * caso faz sentido perguntar o desfecho — o veículo está saindo de um serviço não concluído.
   */
  const servicoAtualEmAberto = useMemo(() => {
    const esteiraAtualId = carData?.esteiraId;
    if (!esteiraAtualId || !selectedStatus || selectedStatus === esteiraAtualId) return null;
    const emAberto = (carData?.servicosInacabados ?? []).find(
      (s: any) => s.esteiraId === esteiraAtualId,
    );
    if (emAberto) return emAberto;
    // Fallback pelo planejamento (cobre respostas antigas sem `servicosInacabados`).
    const planned = plannedActivities.find((a) => a.esteiraId === esteiraAtualId);
    return isServicoEmAberto(planned?.estado)
      ? { esteiraId: esteiraAtualId, estado: planned?.estado, esteiraNome: null }
      : null;
  }, [carData, plannedActivities, selectedStatus]);

  const nomeEsteiraAtual =
    servicoAtualEmAberto?.esteiraNome ||
    esteirasAtivas.find((e) => e.id === carData?.esteiraId)?.nome ||
    'esteira atual';

  useEffect(() => {
    if (isOpen) {
      setEstimatedDaysInStatus(4);
      setUseDefaultDays(true);
      setConfirmStep(false);
      setDesfechoServicoAtual(null);
      setMotivoParadaId('');
      setDetalheDaParada('');
      // at-93: cada abertura recomeça no padrão seguro, nunca na escolha da vez anterior.
      setEscolhaColaborador(ESCOLHA_PADRAO);
    } else {
      setPlannedActivities([]);
      setCarData(null);
      setCurrentCarStatus(null);
      setApprovedStatuses([]);
      setCollaboratorSearch('');
      setCollaboratorResults([]);
      setEscolhaColaborador(ESCOLHA_PADRAO);
      setConfirmStep(false);
      setDesfechoServicoAtual(null);
    }
  }, [isOpen]);

  // at-46: serviço já registrado como inacabado começa com "deixar inacabado" pré-selecionado —
  // é a continuidade do que já está valendo; o Chefe pode trocar para "concluído".
  useEffect(() => {
    if (servicoAtualEmAberto?.estado === 'PAUSADA' && desfechoServicoAtual === null) {
      setDesfechoServicoAtual('PAUSADO');
      // at-134: o motivo que já está valendo vem junto — o Chefe não precisa escolher de novo o
      // que o colaborador declarou, só confirmar ou trocar.
      setMotivoParadaId(servicoAtualEmAberto?.motivoParada?.id ?? '');
      setDetalheDaParada(servicoAtualEmAberto?.observacao ?? '');
    }
  }, [servicoAtualEmAberto, desfechoServicoAtual]);

  useEffect(() => {
    if (!isOpen || !carId) return;
    setLoadingActivities(true);

    Promise.all([
      getAtividadesCarro({ carId, isActive: true }),
      getCar(carId),
      getCarStatusHistory(carId),
    ])
      .then(([activities, data, historyData]) => {
        setPlannedActivities(
          (activities || []).sort((a: PlannedActivity, b: PlannedActivity) => (a.order ?? 999) - (b.order ?? 999))
        );
        setCarData(data);
        setCurrentCarStatus(data?.status || null);
        const approved = (historyData?.history || [])
          .filter((h: any) => h.approvalStatus === 'APPROVED')
          .map((h: any) => h.esteiraId || h.status);
        setApprovedStatuses(approved);
      })
      .catch((error) => {
        // Sem sinal, o modal de troca de esteira abria sem planejamento nem
        // histórico — como se o veículo não tivesse nada em aberto.
        setPlannedActivities([]);
        setCarData(null);
        setCurrentCarStatus(null);
        setApprovedStatuses([]);
        notify.errorFrom(
          error,
          'Não foi possível carregar as informações do veículo. Feche e abra novamente; se persistir, acione a TI.',
        );
      })
      .finally(() => setLoadingActivities(false));
  }, [isOpen, carId]);

  /**
   * at-62: quem pode trabalhar na esteira destino. Reconsultado a cada troca de seleção — o Chefe
   * pode escolher Desmontagem, ver a lista, e depois trocar para Pintura.
   *
   * Fail-open (mesmo princípio do bloqueio de pausas da at-48b): falha deixa `null`, a busca segue
   * completa e o aviso abaixo do campo declara que não foi possível verificar. A imposição real é
   * do backend.
   */
  useEffect(() => {
    if (!showCollaboratorPicker || !targetEst?.id) {
      setIdsElegiveisDestino(null);
      setElegiveisDestino(null);
      setElegibilidadeIndisponivel(false);
      return;
    }

    let cancelado = false;
    // at-99: o `carId` soma as esteiras abertas em paralelo, que continuam vigentes quando o
    // destino é esteira de trabalho — é o conjunto que o backend avalia ao mover o veículo.
    getColaboradoresElegiveis(targetEst.id, carId ?? undefined)
      .then(({ regraAtiva, elegiveis }) => {
        if (cancelado) return;
        setElegibilidadeIndisponivel(false);
        setIdsElegiveisDestino(regraAtiva ? elegiveis.map((c) => c.id) : null);
        setElegiveisDestino(
          regraAtiva ? elegiveis.map((c) => ({ id: c.id, fullName: c.fullName ?? null })) : null,
        );
      })
      .catch(() => {
        if (cancelado) return;
        setIdsElegiveisDestino(null);
        setElegiveisDestino(null);
        setElegibilidadeIndisponivel(true);
      });

    return () => {
      cancelado = true;
    };
  }, [showCollaboratorPicker, targetEst?.id, carId]);

  /** at-62: o colaborador não atende a esteira destino? */
  const semEspecialidade = (id: string) =>
    idsElegiveisDestino !== null && !idsElegiveisDestino.includes(id);

  /** "A", "A e B", "A, B e C" — para as frases saírem em português de gente. */
  const listarNomes = (nomes: string[]): string =>
    nomes.length <= 1
      ? nomes[0] ?? ''
      : `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;

  /**
   * O que acontece com CADA colaborador já vinculado, na esteira destino escolhida.
   *
   * Pedido do Victor (07/08/2026): o sistema avisava quem **sai** (perda de especialidade) e ficava
   * calado sobre quem **fica** — então o Chefe trocava a esteira sem perceber que deixava alguém
   * trabalhando ali. Aqui as duas metades são declaradas antes de confirmar.
   *
   * Também deixa de ser silencioso o caso mais antigo: destino que **não aceita colaborador**
   * (Entrega, Aguardando Entrega) desvincula TODOS — comportamento da at-45 que nunca foi dito na
   * tela.
   */
  const efeitoNosColaboradores = useMemo(() => {
    const vinculados: Array<{ id: string; fullName: string | null }> = carData?.colaboradores ?? [];
    if (!selectedStatus) return null;

    const nomeDe = (c: { fullName: string | null }) => c.fullName ?? 'Colaborador';
    const destinoNome = targetEst?.nome ?? 'a esteira selecionada';

    // Veículo sem ninguém vinculado: só há efeito a descrever se alguém foi escolhido.
    if (!vinculados.length) {
      return colaboradorEscolhido
        ? {
            tipo: 'apenas-entrada' as const,
            texto:
              `${colaboradorEscolhido.fullName ?? 'O colaborador escolhido'} entra no veículo como ` +
              `responsável em ${destinoNome}.`,
          }
        : null;
    }

    /**
     * ⚠️ Este ramo vem ANTES do de "Não atribuir" de propósito: quando a esteira destino não
     * aceita colaborador, o campo de escolha nem é exibido e a escolha fica no padrão — mas o que
     * o Chefe precisa ler é o MOTIVO (a esteira não aceita ninguém), não a escolha que ele não
     * fez. Inverter a ordem troca a informação específica pela genérica.
     *
     * ⚠️ As frases são de gênero NEUTRO ("sai do veículo", e não "será desvinculado"): o sistema
     * não sabe o gênero de quem está no cadastro, e concordar errado ("Maria será desvinculado")
     * lê como defeito. Vale para todo texto novo desta feature.
     */
    if (targetEst?.permiteColaborador === false) {
      return {
        tipo: 'todos-saem' as const,
        texto:
          `${listarNomes(vinculados.map(nomeDe))} ${vinculados.length === 1 ? 'sai' : 'saem'} do ` +
          `veículo: a esteira ${destinoNome} não aceita colaborador. As atividades em aberto são ` +
          'encerradas e as horas vão para aprovação.',
      };
    }

    /**
     * at-93: com "Não atribuir" (o padrão), TODOS saem — e é isso que o aviso tem de dizer.
     *
     * Vem antes da checagem de "destino = esteira atual" porque a escolha retira quem está no
     * veículo mesmo que ele não se mova; e antes do ramo de especialidade porque, aqui, ter ou não
     * a especialidade do destino é irrelevante — ninguém fica.
     */
    if (escolhaColaborador.modo === 'NAO_ATRIBUIR') {
      return {
        tipo: 'todos-saem' as const,
        texto:
          `${listarNomes(vinculados.map(nomeDe))} ${vinculados.length === 1 ? 'sai' : 'saem'} do ` +
          `veículo, que segue em ${destinoNome} sem colaborador. As atividades em aberto são ` +
          'encerradas e as horas vão para aprovação.',
      };
    }

    // Destino = esteira atual (não há movimento) e o Chefe optou por manter: nada a dizer.
    if (selectedStatus === carData?.esteiraId) return null;

    // Não é possível afirmar nada quando a verificação falhou — erro de consulta não pode virar
    // afirmação de negócio ("todos continuam").
    if (elegibilidadeIndisponivel) {
      return {
        tipo: 'indisponivel' as const,
        texto:
          `O veículo tem ${vinculados.length === 1 ? 'um colaborador' : `${vinculados.length} colaboradores`} ` +
          `(${listarNomes(vinculados.map(nomeDe))}), mas não foi possível verificar agora quem tem a ` +
          `especialidade de ${destinoNome}. Confira o veículo depois da mudança.`,
      };
    }

    const ficam = vinculados.filter((c) => !semEspecialidade(c.id)).map(nomeDe);
    const saem = vinculados.filter((c) => semEspecialidade(c.id)).map(nomeDe);

    return {
      tipo: 'detalhado' as const,
      ficam,
      saem,
      textoFicam: ficam.length
        ? `${listarNomes(ficam)} ${ficam.length === 1 ? 'continuará' : 'continuarão'} como ` +
          `${ficam.length === 1 ? 'responsável' : 'responsáveis'} pelo veículo em ${destinoNome}.`
        : null,
      textoSaem: saem.length
        ? `${listarNomes(saem)} ${saem.length === 1 ? 'sai' : 'saem'} do veículo por não ter a ` +
          `especialidade de ${destinoNome}. As atividades em aberto são encerradas e as horas vão ` +
          'para aprovação.'
        : null,
      /**
       * at-93: escolher alguém SOMA (at-45/ADR 0020) — não substitui quem já está. Isso nunca
       * esteve dito em tela, e é vizinho do problema que a entrega resolve: o Chefe escolhe uma
       * pessoa para a esteira nova e leva as outras junto sem saber.
       */
      textoAdicionado:
        colaboradorEscolhido && ficam.length
          ? `${colaboradorEscolhido.fullName ?? 'O colaborador escolhido'} entra no veículo, ` +
            `somado a quem continua — a escolha não substitui ${listarNomes(ficam)}.`
          : null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    carData,
    selectedStatus,
    targetEst,
    idsElegiveisDestino,
    elegibilidadeIndisponivel,
    escolhaColaborador,
  ]);

  // Troca de esteira destino invalida uma escolha que era válida na esteira anterior — deixar o
  // nome no campo faria o Chefe confirmar acreditando que a pessoa entra no veículo.
  useEffect(() => {
    if (colaboradorEscolhido && semEspecialidade(colaboradorEscolhido.collaboratorId)) {
      // at-93: volta para o padrão seguro, não para "manter" — a pessoa deixou de ser elegível,
      // e nada foi dito sobre quem já está no veículo.
      setEscolhaColaborador(ESCOLHA_PADRAO);
      notify.warning(
        `${colaboradorEscolhido.fullName ?? 'O colaborador escolhido'} não tem a especialidade de ` +
          `${targetEst?.nome ?? 'esta esteira'} e saiu da seleção. Escolha outro colaborador ou ` +
          'siga sem colaborador.',
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsElegiveisDestino, colaboradorEscolhido?.collaboratorId]);

  /**
   * Fecha a lista ao clicar fora dela. `mousedown` é o mesmo evento que o `onMouseDown` das
   * opções usa — e as opções estão DENTRO do `ref`, então escolher alguém não passa por aqui.
   *
   * ⚠️ Sem Escape de propósito: o listener dos modais hospedeiros vive em `document`, e
   * `stopPropagation` de evento sintético do React não o alcança — fechar a lista fecharia o
   * modal junto, que é pior do que o defeito original.
   */
  useEffect(() => {
    if (!listaColaboradoresAberta) return undefined;
    const cliqueFora = (evento: MouseEvent) => {
      if (buscaColaboradorRef.current?.contains(evento.target as Node)) return;
      setListaColaboradoresAberta(false);
    };
    document.addEventListener('mousedown', cliqueFora);
    return () => document.removeEventListener('mousedown', cliqueFora);
  }, [listaColaboradoresAberta]);

  /**
   * Trocar de esteira destino recolhe a lista (o conjunto de elegíveis é outro) e, em esteira de
   * TRABALHO, a reabre assim que os elegíveis chegam — é a decisão de 15/09/2026.
   *
   * 🔴 O critério é `sistema`, não um número de nomes: esteira de fluxo livre não é restringida
   * por especialidade, então ali a "lista de elegíveis" é a oficina inteira e abrir sozinha
   * esconde o resto do modal. Limiar por quantidade seria arbitrário e mentiria sobre o motivo.
   */
  useEffect(() => {
    setListaColaboradoresAberta(false);
  }, [selectedStatus]);

  useEffect(() => {
    if (!showCollaboratorPicker) return;
    if (!elegiveisDestino || elegiveisDestino.length === 0) return;
    if (targetEst?.sistema === true) return;
    setListaColaboradoresAberta(true);
  }, [showCollaboratorPicker, elegiveisDestino, targetEst?.sistema]);

  useEffect(() => {
    if (!showCollaboratorPicker) {
      setCollaboratorResults([]);
      return;
    }

    /**
     * Lista restrita (15/09/2026): quem pode trabalhar no destino já está em mãos, então a busca
     * é local — e o campo VAZIO mostra todos eles, em vez de exigir duas letras para revelar uma
     * lista que costuma ter três nomes. Era esse o pedido: ver quem pode, não descobrir digitando.
     */
    if (elegiveisDestino) {
      setCollaboratorResults(
        elegiveisDestino.filter((c) => casaComBusca(c.fullName, collaboratorSearch)),
      );
      return;
    }

    if (collaboratorSearch.trim().length < 2) {
      setCollaboratorResults([]);
      return;
    }

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      setSearchingCollaborator(true);
      try {
        const result = await searchCollaborators(collaboratorSearch.trim(), 1, 6);
        setCollaboratorResults(result?.users ?? result ?? []);
      } catch (error) {
        setCollaboratorResults([]);
        notify.errorFrom(
          error,
          'Não foi possível buscar colaboradores. Tente novamente; se persistir, acione a TI.',
        );
      } finally {
        setSearchingCollaborator(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [collaboratorSearch, showCollaboratorPicker, elegiveisDestino]);

  const allAvailableStatuses = useMemo(() => {
    return esteirasAtivas.map((est) => {
      const planned = plannedActivities.find((a) => a.esteiraId === est.id || a.status === est.chave);
      return {
        id: est.id,
        status: est.chave,
        nome: est.nome,
        sistema: est.sistema,
        order: planned ? planned.order : null,
        // at-46: estado de execução do serviço nesta esteira (fonte de "realizada").
        estado: planned?.estado,
      };
    });
  }, [esteirasAtivas, plannedActivities]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedNode = event.target as Node;
      if (!document.contains(clickedNode)) return;
      const isOutsideModal = modalRef.current && !modalRef.current.contains(clickedNode);
      const isOutsideSidebar = sidebarRef?.current && !sidebarRef.current.contains(clickedNode);
      if (isOutsideModal && isOutsideSidebar) onCancel();
    };

    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    else document.removeEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onCancel, sidebarRef]);

  if (!isOpen) return null;

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStatus || (isChefe && !useDefaultDays && estimatedDaysInStatus < 1)) return;

    // at-46: sair de uma esteira com serviço em aberto exige declarar o desfecho — o sistema
    // não decide sozinho se o serviço foi concluído.
    if (servicoAtualEmAberto && !desfechoServicoAtual) {
      notify.warning(`Informe o que acontece com o serviço de ${nomeEsteiraAtual} antes de continuar.`);
      return;
    }

    // at-134: o motivo da parada é conferido aqui, antes da etapa de confirmação — o backend o
    // exige e recusaria a mudança inteira sem ele.
    if (pendenciaDaParada) {
      notify.warning(pendenciaDaParada);
      return;
    }

    if (targetEst?.chave === 'ENTREGA' && carId) {
      try {
        await getLatestChecklistSessionByCar(carId, { type: 'SAIDA' });
      } catch {
        notify.error('Checklist de saída ainda não foi realizado para este veículo.');
        return;
      }
    }

    setConfirmStep(true);
  };

  const handleFinalConfirm = () => {
    // undefined → o backend aplica o slaDiasPadrao da esteira destino (fallback 4)
    const days = isChefe && !useDefaultDays ? estimatedDaysInStatus : undefined;
    /**
     * at-93: os dois últimos argumentos são mutuamente exclusivos por construção.
     *
     * `deixarSemColaborador` só sai quando o campo de colaborador está VISÍVEL: em esteira que não
     * aceita colaborador (Entrega, Aguardando Entrega) o backend já desvincula todos, e pedir o
     * desvínculo por fora seria uma chamada a mais para o mesmo efeito.
     */
    const naoAtribuir = showCollaboratorPicker && escolhaColaborador.modo === 'NAO_ATRIBUIR';
    // at-134: o motivo só viaja quando há o que mandar (catálogo vazio = nada).
    const motivo = motivoDaParadaParaEnviar(desfechoServicoAtual, motivoParadaId, detalheDaParada);
    onConfirm(
      days,
      colaboradorEscolhido?.collaboratorId,
      desfechoServicoAtual ?? undefined,
      naoAtribuir || undefined,
      ...(motivo ? [motivo] : []),
    );
  };

  const handleSelectCollaborator = (collab: any) => {
    setEscolhaColaborador({
      modo: 'ADICIONAR',
      collaboratorId: collab.id,
      fullName: collab.fullName || collab.username || null,
    });
    setCollaboratorSearch('');
    setCollaboratorResults([]);
    setListaColaboradoresAberta(false);
  };

  const title = isChefe ? 'Alterar esteira' : 'Solicitar alteração de esteira';

  /**
   * Bloco "o que acontece com os colaboradores do veículo" — renderizado nas DUAS etapas do modal
   * (escolha e confirmação), de propósito: na escolha ele informa a decisão; na confirmação ele é
   * o aviso final antes de executar (padrão do at-18).
   */
  const avisoColaboradores = efeitoNosColaboradores && (
    <div className="status-modal-colaboradores-aviso">
      {efeitoNosColaboradores.tipo === 'detalhado' ? (
        <>
          {efeitoNosColaboradores.textoFicam && <p>{efeitoNosColaboradores.textoFicam}</p>}
          {efeitoNosColaboradores.textoSaem && <p>{efeitoNosColaboradores.textoSaem}</p>}
          {/* at-93: a soma da at-45 dita em voz alta. */}
          {efeitoNosColaboradores.textoAdicionado && (
            <p>{efeitoNosColaboradores.textoAdicionado}</p>
          )}
        </>
      ) : (
        <p>{efeitoNosColaboradores.texto}</p>
      )}
    </div>
  );

  if (confirmStep && selectedStatus) {
    return (
      <div className="status-modal-overlay">
        <div className="status-modal-content" ref={modalRef}>
          <div className="LightboxStatus">
            <div className="LightboxStatusForm">
              <div className="EditarCarro">{title}</div>
              <p className="status-modal-confirm-message">
                Tem certeza de que deseja enviar para{' '}
                <strong>"{targetEst?.nome || selectedStatus}"</strong>?
              </p>
              {/* at-46: antes de confirmar, o efeito no serviço da esteira de origem. */}
              {servicoAtualEmAberto && (
                <p className="status-modal-servico-aviso">
                  {desfechoServicoAtual === 'PAUSADO'
                    ? `O serviço de ${nomeEsteiraAtual} ficará registrado como inacabado: fica como pendência visível e o veículo poderá voltar para a retomada. As atividades em andamento serão encerradas e as horas irão para aprovação.`
                    : `O serviço de ${nomeEsteiraAtual} será dado como concluído. As atividades em andamento serão encerradas e as horas irão para aprovação.`}
                </p>
              )}
              {/* at-62: quem fica e quem sai do veículo — a metade "quem fica" era silenciosa. */}
              {avisoColaboradores}
              <div className="Frame1000003998Status">
                <div className="CtaBigEditar cancel" onClick={() => setConfirmStep(false)}>
                  <button className="CustomFileCancel" type="button">Voltar</button>
                </div>
                <button
                  onClick={handleFinalConfirm}
                  className="CtaBigEditar save"
                  type="button"
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="status-modal-overlay">
      <div className="status-modal-content" ref={modalRef}>
        <div className="LightboxStatus">
          <form className="LightboxStatusForm">
            <div className="EditarCarro">{title}</div>

            <div className="CampoStatus">
              {loadingActivities ? (
                <div style={{ fontSize: '13px', color: 'var(--tema-texto-3, #6b7280)', padding: '8px 0' }}>
                  Carregando esteiras planejadas...
                </div>
              ) : (
                <div className="controle-producao-next-sector-list">
                  {allAvailableStatuses
                    .filter((activity) => {
                      const isCurrentStatus = activity.id === carData?.esteiraId || activity.status === currentCarStatus;
                      const wasApproved = approvedStatuses.includes(activity.id) || approvedStatuses.includes(activity.status);
                      const isPlanned = activity.order !== null;
                      // Exibe apenas: de sistema (Padrão), previstas, realizadas ou a atual.
                      // Esteiras criadas manualmente só aparecem se estiverem no planejamento.
                      return activity.sistema || isPlanned || wasApproved || isCurrentStatus;
                    })
                    .map((activity) => {
                    const isCurrentStatus = activity.id === carData?.esteiraId || activity.status === currentCarStatus;
                    const wasApproved = approvedStatuses.includes(activity.id) || approvedStatuses.includes(activity.status);
                    const isPlanned = activity.order !== null;

                    let badgeText: string | null = activity.sistema ? 'Padrão' : null;
                    let badgeClass = 'badge-neutral';
                    let leftBorderColor = '#94a3b8';

                    // at-46: "Inacabado" tem precedência — é a pendência que o Chefe precisa
                    // ver ao escolher o destino (e onde ele pode retomar o serviço).
                    if (activity.estado === 'PAUSADA') {
                      badgeText = 'Inacabado';
                      badgeClass = 'badge-purple';
                      leftBorderColor = '#8b5cf6';
                    } else if (isCurrentStatus) {
                      badgeText = 'Atual';
                      badgeClass = 'badge-orange';
                      leftBorderColor = '#ea5d0e';
                    } else if (activity.estado === 'CONCLUIDA' || (!activity.estado && wasApproved)) {
                      // Sem estado (esteira nunca planejada) mantém a leitura pelo histórico.
                      badgeText = 'Realizada';
                      badgeClass = 'badge-green';
                      leftBorderColor = '#10b981';
                    } else if (isPlanned) {
                      badgeText = 'Prevista';
                      badgeClass = 'badge-red';
                      leftBorderColor = '#ef4444';
                    }

                    const showBadge = badgeText !== null;

                    return (
                      <div
                        key={activity.id}
                        className={`controle-producao-next-sector-option${selectedStatus === activity.id ? ' selected' : ''}`}
                        onClick={() => {
                          setSelectedStatus(activity.id);
                          // at-93: trocar de destino recomeça no padrão seguro — a escolha valia
                          // para a esteira anterior.
                          setEscolhaColaborador(ESCOLHA_PADRAO);
                          setCollaboratorSearch('');
                          setCollaboratorResults([]);
                        }}
                        style={{ cursor: 'pointer', borderLeft: `4px solid ${leftBorderColor}` }}
                      >
                        <div className="next-sector-option-name">
                          {activity.nome}
                        </div>
                        {showBadge && <span className={`next-sector-status-badge ${badgeClass}`}>{badgeText}</span>}
                        {selectedStatus === activity.id && <div className="next-sector-check">✓</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* at-46: o veículo está saindo de uma esteira com serviço em aberto — quem move
                declara o desfecho, e cada opção diz o que vai acontecer antes de confirmar. */}
            {servicoAtualEmAberto && (
              <div className="status-modal-servico-desfecho">
                <label className="status-modal-field-label">
                  O que acontece com o serviço de {nomeEsteiraAtual}?
                </label>
                {servicoAtualEmAberto.estado === 'PAUSADA' && (
                  <p className="status-modal-servico-hint">
                    Este serviço já está registrado como inacabado.
                  </p>
                )}
                <div className="controle-producao-next-sector-list">
                  <div
                    className={`controle-producao-next-sector-option${desfechoServicoAtual === 'CONCLUIDO' ? ' selected' : ''}`}
                    onClick={() => setDesfechoServicoAtual('CONCLUIDO')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="next-sector-option-name">Serviço concluído</div>
                    <div className="next-sector-option-desc">A etapa se encerra para o veículo</div>
                    {desfechoServicoAtual === 'CONCLUIDO' && <div className="next-sector-check">✓</div>}
                  </div>
                  <div
                    className={`controle-producao-next-sector-option${desfechoServicoAtual === 'PAUSADO' ? ' selected' : ''}`}
                    onClick={() => setDesfechoServicoAtual('PAUSADO')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="next-sector-option-name">Deixar inacabado</div>
                    <div className="next-sector-option-desc">
                      Fica pendente e o veículo pode voltar para retomar
                    </div>
                    {desfechoServicoAtual === 'PAUSADO' && <div className="next-sector-check">✓</div>}
                  </div>
                </div>
                {desfechoServicoAtual === 'PAUSADO' && (
                  <MotivoDaParada
                    idPrefixo="status-modal-parada"
                    motivos={motivosDaParada}
                    estado={estadoDosMotivos}
                    motivoId={motivoParadaId}
                    onMotivo={setMotivoParadaId}
                    detalhe={detalheDaParada}
                    onDetalhe={setDetalheDaParada}
                  />
                )}
                {desfechoServicoAtual && (
                  <p className="status-modal-servico-aviso">
                    {desfechoServicoAtual === 'PAUSADO'
                      ? `O serviço de ${nomeEsteiraAtual} fica como pendência visível e retomável quando o veículo voltar. As atividades em andamento são encerradas e as horas vão para aprovação.`
                      : `O serviço de ${nomeEsteiraAtual} é dado como realizado. As atividades em andamento são encerradas e as horas vão para aprovação.`}
                  </p>
                )}
              </div>
            )}

            {isChefe && (
              <div className="status-modal-days-field">
                <label className="status-modal-field-label">Previsão de dias neste status</label>
                <label className="status-modal-default-days-toggle">
                  <input
                    type="checkbox"
                    checked={useDefaultDays}
                    onChange={(e) => {
                      setUseDefaultDays(e.target.checked);
                      if (!e.target.checked) setEstimatedDaysInStatus(defaultDays);
                    }}
                  />
                  <span>
                    Utilizar dias padrão da esteira{targetEst ? ` (${defaultDays} ${defaultDays === 1 ? 'dia' : 'dias'})` : ''}
                  </span>
                </label>
                {!useDefaultDays && (
                  <input
                    type="number"
                    min={1}
                    value={estimatedDaysInStatus}
                    onChange={(e) => setEstimatedDaysInStatus(Math.max(1, Number(e.target.value)))}
                    className="status-modal-days-input"
                  />
                )}
              </div>
            )}

            {/* at-62: o efeito nos colaboradores JÁ vinculados aparece assim que a esteira é
                escolhida — não só na confirmação. É aqui que o Chefe decide, e a informação que
                faltava ("quem continua") é justamente a que evita mover o veículo sem perceber que
                deixou alguém trabalhando. Fora do `showCollaboratorPicker` de propósito: destino
                que NÃO aceita colaborador esconde aquele campo, e é exatamente o caso em que
                avisar que todos serão desvinculados importa mais. */}
            {avisoColaboradores}

            {showCollaboratorPicker && (
              <div className="status-modal-collaborator-field">
                <label className="status-modal-field-label">Colaborador do veículo na esteira nova</label>

                {/* at-93: as duas opções explícitas vêm ANTES da busca, e "Não atribuir" nasce
                    marcada — mover o veículo levando o colaborador atual passou a ser ato
                    deliberado. Reusa as classes do desfecho de serviço (at-46), que já vivem
                    neste modal, em vez de criar visual novo. */}
                <div className="controle-producao-next-sector-list">
                  <div
                    className={`controle-producao-next-sector-option${escolhaColaborador.modo === 'NAO_ATRIBUIR' ? ' selected' : ''}`}
                    onClick={() => setEscolhaColaborador(ESCOLHA_PADRAO)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="next-sector-option-name">
                      {TEXTOS_ESCOLHA_COLABORADOR.naoAtribuir.rotulo}
                    </div>
                    <div className="next-sector-option-desc">
                      {podeManterColaboradores
                        ? TEXTOS_ESCOLHA_COLABORADOR.naoAtribuir.descricaoComVinculados
                        : TEXTOS_ESCOLHA_COLABORADOR.naoAtribuir.descricaoSemVinculados}
                    </div>
                    {escolhaColaborador.modo === 'NAO_ATRIBUIR' && (
                      <div className="next-sector-check">✓</div>
                    )}
                  </div>
                  {podeManterColaboradores && (
                    <div
                      className={`controle-producao-next-sector-option${escolhaColaborador.modo === 'MANTER' ? ' selected' : ''}`}
                      onClick={() => setEscolhaColaborador({ modo: 'MANTER' })}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="next-sector-option-name">
                        {TEXTOS_ESCOLHA_COLABORADOR.manter.rotulo}
                      </div>
                      <div className="next-sector-option-desc">
                        {TEXTOS_ESCOLHA_COLABORADOR.manter.descricao}
                      </div>
                      {escolhaColaborador.modo === 'MANTER' && (
                        <div className="next-sector-check">✓</div>
                      )}
                    </div>
                  )}
                </div>

                {/* at-45: atribuir SOMA — esta busca adiciona um colaborador ao veículo.
                    A gestão completa (vários, remoção individual) fica em "Definir Colaboradores". */}
                <label className="status-modal-field-label">
                  {TEXTOS_ESCOLHA_COLABORADOR.escolher.rotulo}
                </label>

                {colaboradorEscolhido ? (
                  <div className="status-modal-collaborator-chip">
                    <span>{colaboradorEscolhido.fullName ?? 'Colaborador'}</span>
                    <button
                      type="button"
                      className="status-modal-collaborator-chip-remove"
                      aria-label="Tirar o colaborador da seleção"
                      onClick={() => {
                        setEscolhaColaborador(ESCOLHA_PADRAO);
                        // Sem isto a lista voltaria ABERTA ao tirar o chip, que é o defeito.
                        setListaColaboradoresAberta(false);
                      }}
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="status-modal-collaborator-search-wrapper" ref={buscaColaboradorRef}>
                    <input
                      type="text"
                      placeholder="Buscar colaborador..."
                      value={collaboratorSearch}
                      onChange={(e) => {
                        setCollaboratorSearch(e.target.value);
                        setListaColaboradoresAberta(true);
                      }}
                      onFocus={() => setListaColaboradoresAberta(true)}
                      className="status-modal-collaborator-input"
                    />
                    {searchingCollaborator && (
                      <div className="status-modal-collaborator-hint">Buscando...</div>
                    )}
                    {listaColaboradoresAberta && collaboratorResults.length > 0 && (
                      <div className="status-modal-collaborator-dropdown">
                        {/* 15/09/2026: a lista traz APENAS quem atende a esteira DESTINO (e as
                            abertas em paralelo). Até aqui os demais apareciam com o motivo e
                            desabilitados — o que a at-62 preferiu, e que na prática enterrava
                            quem podia ser escolhido no meio da oficina inteira. */}
                        {collaboratorResults.map((c: any) => (
                          <div
                            key={c.id}
                            className="status-modal-collaborator-option"
                            onMouseDown={() => handleSelectCollaborator(c)}
                          >
                            {c.fullName || c.username}
                          </div>
                        ))}
                      </div>
                    )}
                    {!searchingCollaborator &&
                      collaboratorResults.length === 0 &&
                      (elegiveisDestino
                        ? collaboratorSearch.trim().length > 0
                        : collaboratorSearch.trim().length >= 2) && (
                        <div className="status-modal-collaborator-hint">Nenhum colaborador encontrado.</div>
                      )}
                  </div>
                )}

                {/* at-62: esteira que nenhuma especialidade cobre não recebe ninguém. Dizer isso
                    aqui evita a busca inútil — sem o aviso, a lista apareceria cheia de nomes
                    todos bloqueados, sem explicar por quê. */}
                {idsElegiveisDestino !== null && idsElegiveisDestino.length === 0 && (
                  <div className="status-modal-collaborator-hint">
                    Nenhum colaborador tem a especialidade de {targetEst?.nome ?? 'esta esteira'}, então
                    o veículo seguirá sem colaborador. Para liberar alguém, cadastre a especialidade em
                    Administração do Sistema.
                  </div>
                )}

                {elegibilidadeIndisponivel && (
                  <div className="status-modal-collaborator-hint">
                    Não foi possível verificar as especialidades agora. A lista está completa; se o
                    colaborador escolhido não atender esta esteira, ele não será adicionado e o
                    sistema avisará.
                  </div>
                )}
              </div>
            )}

            <div className="Frame1000003998Status">
              <div className="CtaBigEditar cancel" onClick={onCancel}>
                <button className="CustomFileCancel" type="button">Cancelar</button>
              </div>
              <button
                onClick={handleConfirm}
                className="CtaBigEditar save"
                type="button"
                disabled={!selectedStatus || (isChefe && !useDefaultDays && estimatedDaysInStatus < 1)}
              >
                Confirmar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StatusModal;

